import React, { useEffect, useRef, useState } from 'react';
import { Modal, Space, message } from 'antd';
import Search from './Search';
import SearchResults from './SearchResults';
import Map from './Map';
import { getUserLocation, loadMapScript, jsonp } from './utils';
import styles from './styles/index.module.css';

const KEY = '6SXBZ-RVL6C-LVC24-AQHCM-YMBYQ-UCF4P';

const LocationPicker = ({ visible, onCancel, onOk, initialLocation }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);

  // 重置所有状态
  const resetState = () => {
    setKeyword('');
    setSelectedLocation(initialLocation || null);
    setLoading(false);
    setMapLoading(true);
    setSearchResults([]);

    if (map) {
      map.destroy();
      setMap(null);
    }
    if (marker) {
      marker.setMap(null);
      setMarker(null);
    }
    if (mapRef.current) {
      mapRef.current.innerHTML = '';
    }

    const existingScript = document.querySelector('script[src*="map.qq.com/api/gljs"]');
    if (existingScript) {
      document.head.removeChild(existingScript);
    }
    if (window.TMap) {
      window.TMap = undefined;
    }
  };

  // 处理取消
  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // 处理位置选择
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    if (map) {
      map.setCenter(new window.TMap.LatLng(location.latitude, location.longitude));
      map.setZoom(16);
    }
    if (marker) {
      marker.updateGeometries([{
        id: 'selected_location',
        styleId: 'marker',
        position: new window.TMap.LatLng(location.latitude, location.longitude),
        properties: {
          title: '选中位置'
        }
      }]);
    }
    setSearchResults([]);
  };

  // 处理搜索
  const handleSearch = async (value) => {
    if (!value) return;

    try {
      setLoading(true);
      const center = map ? map.getCenter() : null;
      const searchLat = center ? center.getLat() : (initialLocation?.latitude || 39.908802);
      const searchLng = center ? center.getLng() : (initialLocation?.longitude || 116.397502);

      const data = await jsonp(
        `https://apis.map.qq.com/ws/place/v1/search?keyword=${encodeURIComponent(value)}&boundary=nearby(${searchLat},${searchLng},1000)&page_size=20&page_index=1&key=${KEY}`
      );

      if (data.status === 0 && data.data) {
        const locations = data.data.map(item => ({
          title: item.title,
          address: item.address,
          latitude: item.location.lat,
          longitude: item.location.lng
        }));
        setSearchResults(locations);
      } else {
        message.error('搜索失败：' + data.message);
      }
    } catch (error) {
      console.error('Search error:', error);
      message.error('搜索失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始化地图
  useEffect(() => {
    if (!visible) {
      resetState();
      return;
    }

    let mounted = true;

    const initMap = async () => {
      try {
        setMapLoading(true);
        await loadMapScript(KEY);

        if (!mounted) return;

        let centerLocation = initialLocation;
        if (!centerLocation) {
          const userLocation = await getUserLocation();
          if (userLocation) {
            centerLocation = userLocation;
          }
        }

        const mapInstance = new window.TMap.Map(mapRef.current, {
          center: centerLocation
            ? new window.TMap.LatLng(centerLocation.latitude, centerLocation.longitude)
            : new window.TMap.LatLng(39.908802, 116.397502),
          zoom: 12,
          pitch: 0,
          rotation: 0,
          showControl: false,
          baseMap: {
            type: 'vector',
            features: ['base', 'building3d', 'point'],
            showLabel: true
          }
        });

        const markerLayer = new window.TMap.MultiMarker({
          map: mapInstance,
          enableDefaultStyle: false,
          styles: {
            'marker': new window.TMap.MarkerStyle({
              width: 32,
              height: 32,
              anchor: { x: 16, y: 32 },
              src: 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/location.png'
            })
          },
          geometries: centerLocation ? [{
            id: 'selected_location',
            styleId: 'marker',
            position: new window.TMap.LatLng(centerLocation.latitude, centerLocation.longitude),
            properties: {
              title: '当前位置'
            }
          }] : []
        });

        if (centerLocation) {
          setSelectedLocation(centerLocation);
        }

        mapInstance.on('click', async (evt) => {
          const lat = evt.latLng.getLat();
          const lng = evt.latLng.getLng();

          markerLayer.updateGeometries([{
            id: 'selected_location',
            styleId: 'marker',
            position: new window.TMap.LatLng(lat, lng),
            properties: {
              title: '选中位置'
            }
          }]);

          try {
            const data = await jsonp(
              `https://apis.map.qq.com/ws/geocoder/v1?location=${lat},${lng}&key=${KEY}`
            );

            if (data.status === 0 && data.result) {
              const { formatted_addresses } = data.result;
              setSelectedLocation({
                address: formatted_addresses?.recommend || data.result.address,
                latitude: lat,
                longitude: lng
              });
            } else {
              message.error('获取地址信息失败：' + data.message);
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            message.error('获取地址信息失败，请稍后重试');
          }
        });

        setMap(mapInstance);
        setMarker(markerLayer);
      } catch (error) {
        console.error('Map initialization error:', error);
        message.error('地图初始化失败，请重试');
      } finally {
        if (mounted) {
          setMapLoading(false);
        }
      }
    };

    initMap();

    return () => {
      mounted = false;
      resetState();
    };
  }, [visible, initialLocation]);

  // 确认选择
  const handleOk = () => {
    if (!selectedLocation) {
      message.warning('请先选择位置');
      return;
    }
    onOk(selectedLocation);
  };

  return (
    <Modal
      title="选择位置"
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      className={styles.locationPickerModal}
      bodyStyle={{
        height: 'auto',
        padding: 24,
        overflow: 'hidden'
      }}
      maskStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.45)'
      }}
      maskClosable={false}
      destroyOnClose
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search
            value={keyword}
            onChange={setKeyword}
            onSearch={handleSearch}
            loading={loading}
            disabled={mapLoading}
          />
          <SearchResults
            results={searchResults}
            onSelect={handleLocationSelect}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Map
            mapRef={mapRef}
            loading={loading || mapLoading}
            loadingTip={mapLoading ? "地图加载中..." : "搜索中..."}
          />
          {selectedLocation && (
            <div className={styles.selectedLocation}>
              <span className={styles.selectedLocationLabel}>已选位置：</span>
              <span style={{ wordBreak: 'break-all' }}>{selectedLocation.address}</span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LocationPicker; 